import { blockPalette, blockTheme, ContentBlockStyles } from "@gaiaProfessionals/Widgets/HCP/ContentBlocks/HCPContentBlocks.styles";
import HCPFeaturedItem from "@gaiaProfessionals/Widgets/HCP/ContentBlocks/HCPFeaturedItem";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import React, { FC } from "react";
import styled, { css } from "styled-components";

// <============== Fields Data ==============>
const enum fields {
    featuredItemsAmount = "featuredItemsAmount",
    blockTheme = "blockTheme",
    featuredHeader = "featuredHeader",
    featuredSubHeader = "featuredSubHeader",
    btnLabel = "btnLabel",
    btnLink = "btnLink"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.featuredItemsAmount]: "1" | "2" | "3" | "4";
        [fields.blockTheme]: "light" | "dark";
        [fields.featuredHeader]: string;
        [fields.featuredSubHeader]: string;
        [fields.btnLabel]: string;
        [fields.btnLink]: LinkFieldValue;
    };
}

// <============== Featured Block Styled Component ==============>
interface FeaturedProps {
    fields: {
        [fields.blockTheme]: "light" | "dark";
    };
}

const FeaturedBlock = styled.div<FeaturedProps>`
    ${blockTheme.rowMargin}
    padding: 4rem 5%;
    width: 100%;
    ${props => props.fields.blockTheme === "light"
        ? css`
            background: ${blockPalette.greenWhite};
            color: ${blockPalette.green};`
        : css`
            background: ${blockPalette.darkGreen};
            background: linear-gradient(2deg, ${blockPalette.darkGreen} 0%, ${blockPalette.lightGreen} 100%);
            color: ${blockPalette.white};`
    }
    text-align: center;
    // Media Queries
    /* Small Devices, Tablets */
    @media screen and (min-width: 768px) {
        padding: 4rem 0%;
    }

    /* =============== Featured Block Wrapper =============== */
    .featuredBlockWrap {
        ${blockTheme.container}
        margin: 2rem auto 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex-wrap: wrap;
        // Media Queries
        /* Small Devices, Tablets */
            @media screen and (min-width: 768px) {
            margin: 2rem auto;
            flex-direction: row;
        }
    }

    /* =============== Featured Block =============== */
    .featuredBlock {
        width: 100%;
        margin-bottom: 2rem;
        // Media Queries
        /* Small Devices, Tablets */
        @media screen and (min-width: 768px) {
            width: 50%;
            margin-bottom: 0;
            // Pseudo Elements
            &:last-of-type {
                margin-right: 0;
            }
        }
        /* Large Devices, Wide Screens */
        @media screen and (min-width: 1200px) {
            width: 25%;
        }
    }

    /* =============== Text Block =============== */
    .textBlock__head {
        font-size: 2rem;
        line-height: 1.15;
        margin-bottom: .5rem;
        text-decoration: underline transparent;
        ${props => props.fields.blockTheme === "light"
            ? css`
                color: ${blockPalette.green};
                text-decoration-color: ${blockPalette.greenWhite};`
            : css`
                color: ${blockPalette.white};`
        }
        transition: text-decoration 0.3s ease-in-out;
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            font-size: 2rem;
        }
        /* Large Devices */
        @media screen and (min-width: 1200px) {
            font-size: 2.5rem;
        }
        /* Largest Devices, Wide Screens */
        @media screen and (min-width: 1400px) {
            font-size: 3rem;
        }
    }
    a:focus .textBlock__head,
    a:hover .textBlock__head {
        transition: text-decoration 0.3s ease-in-out;
        ${props => props.fields.blockTheme === "light"
            ? css`
                text-decoration-color: ${blockPalette.green};`
            : css`
                text-decoration-color: ${blockPalette.white};`
        }
    }

    .textBlock__subHead {
        font-size: 1.25rem;
        font-weight: 600;
        ${props => props.fields.blockTheme === "light"
            ? css`
                color: ${blockPalette.green};`
            : css`
                color: ${blockPalette.white};`
        }
    }
`;

// <============== Component ==============>
const HCPFeatured: FC<OwnProps> = ({ fields, id }) => {

    const { url } = useGetLink(fields.btnLink);

    return (
        <ContentBlockStyles>
            <FeaturedBlock fields={fields} className={fields.blockTheme}>
                <div className="textBlock">
                    {fields.featuredHeader && <h2 className="textBlock__head">{fields.featuredHeader}</h2>}
                    {fields.featuredSubHeader && <h3 className="textBlock__subHead">{fields.featuredSubHeader}</h3>}
                </div>
                <div className="featuredBlockWrap">
                    {+fields.featuredItemsAmount >= 1 && (<div className="featuredBlock">
                        <Zone contentId={id} zoneName="featuredOne"></Zone>
                    </div>)}
                    {+fields.featuredItemsAmount >= 2 && (<div className="featuredBlock">
                        <Zone contentId={id} zoneName="featuredTwo"></Zone>
                    </div>)}
                    {+fields.featuredItemsAmount >= 3 && (<div className="featuredBlock">
                        <Zone contentId={id} zoneName="featuredThree"></Zone>
                    </div>)}
                    {+fields.featuredItemsAmount >= 4 && (<div className="featuredBlock">
                        <Zone contentId={id} zoneName="featuredFour"></Zone>
                    </div>)}
                </div>
                {url && (
                    <Link className="btn btn--primary textBlock__btn" href={url}>
                        {fields.btnLabel}
                    </Link>
                )}
            </FeaturedBlock>
        </ContentBlockStyles>
    );
};

// <============== Widget Fields ==============>
const styleTab = {
    displayName: "Style",
    sortOrder: 0,
};
const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const HCPFeaturedBlock: WidgetModule = {
    component: HCPFeatured,
    definition: {
        group: "Common",
        icon: "Banner",
        displayName: "HCP Featured Block",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.featuredItemsAmount,
                displayName: "Number of Items",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" }
                ],
                defaultValue: "1",
                hideEmptyOption: true,
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.blockTheme,
                displayName: "Theme",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" }
                ],
                defaultValue: "dark",
                hideEmptyOption: true,
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.featuredHeader,
                editorTemplate: "TextField",
                displayName: "Header Text",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.featuredSubHeader,
                editorTemplate: "TextField",
                displayName: "Sub Header Text",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.btnLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Learn More",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.btnLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: contentTab,
            },
        ],
    },
};

export default HCPFeaturedBlock;
